import React, {
  useState,
  useEffect,
  useCallback,
  useMemo,
  useContext,
  useReducer
} from 'react';

import { FetchCallbacksContext } from '../../FetchCallbacks';
import useFetchWithCallbacks from '../../../hooks/useFetchWithCallbacks';
import FormBuilderRemindersView from '../view/FormBuilderRemindersView';
import log from '../../../utils/log';
import GeneralContext from '../../GeneralContext';
import sortByUpdatedDate from '../../../utils/sortByUpdatedDate';
import dotProp from 'dot-prop-immutable';
import {
  getValue as getValueFromLocalStorage,
  setValue as setValueFromLocalStorage
} from '../../../utils/LocalStorageUtils';
import Loader from '../../../components/Loader';
import generateDuplicatedReminderName from '../../../utils/generateDuplicationName';
import getUrlParam from '../../../utils/getUrlParam';
export const SortBy = { NAME: 'name', ACTIVE: 'active', LAST_UPDATE: 'update' };

const sortByActiveFirst = (a, b) => {
  if (a.isActive === b.isActive) return 0;
  if (a.isActive) return -1;
  return 1;
};

const sortByName = (a, b) => (a.title ? a.title : '').localeCompare(b.title);

const sort = (array2Sort, sortType) => {
  switch (sortType) {
    case SortBy.ACTIVE:
      array2Sort.sort(sortByActiveFirst);
      break;
    case SortBy.NAME:
      array2Sort.sort(sortByName);
      break;
    case SortBy.LAST_UPDATE:
      array2Sort.sort(sortByUpdatedDate);
      break;
    default:
      break;
  }
};

const remindersReducer = (state, action) => {
  const { type, payload, index } = action;
  let updatedPayload;

  switch (type) {
    case 'load':
      // payload should be an array of reminders
      return [...payload];

    case 'add':
      // for duplicate - add the ne reminder below the duplicated one
      updatedPayload = {
        ...payload,
        metadata: { ...payload.metadata, updatedDate: +new Date() + '' }
      };
      index >= 0
        ? state.splice(index + 1, 0, updatedPayload)
        : state.unshift(updatedPayload);
      return [...state];

    case 'delete':
      // payload should be the object id
      return state.filter(reminderData => reminderData.id !== payload);

    case 'update':
      // payload should be an object with all reminder data
      const reminderId = payload.id;
      const reminderIndex = state.findIndex(
        reminderData => reminderData.id === reminderId
      );
      updatedPayload = {
        ...payload,
        metadata: { ...payload.metadata, updatedDate: +new Date() + '' }
      };
      return dotProp.set(state, reminderIndex, updatedPayload);

    default:
      return state;
  }
};

const FormBuilderRemindersEngine = ({
  form,
  formId
  // setRemindersDirtyCallback
}) => {
  const fetchAPI = useFetchWithCallbacks();
  const { openSnackbar } = useContext(FetchCallbacksContext);

  const [reminders, dispatch] = useReducer(remindersReducer, []);
  const [formAdmins, setFormAdmins] = useState([]);
  const [isCreateNewReminderDialogOpen, setIsCreateNewReminderDialogOpen] =
    useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [newReminderId, setNewReminderId] = useState(' ');
  const [reminderData4TemplateEditor, setReminderData4TemplateEditor] =
    useState({});
  const [sortBy, setSortBy] = useState(
    getValueFromLocalStorage('reminders-sort') || SortBy.ACTIVE
  );
  const [searchValue, setSearchValue] = useState(getUrlParam('reminder') || '');
  const [filteredReminders, setFilteredReminders] = useState([]);

  useEffect(() => {
    loadReminders(true);
    loadFormAdmins();
  }, []);

  useEffect(() => {
    const filtered = reminders.filter(reminderData =>
      reminderData.title
        ? reminderData.title
            .toLowerCase()
            .includes(searchValue.toLowerCase().trim())
        : searchValue === ''
        ? true
        : false
    );
    setFilteredReminders(filtered);
  }, [searchValue, reminders]);

  const loadReminders = useCallback(isInit => {
    setIsLoading(true);
    fetchAPI(
      `/scheduler/v1/schedule` +
        `?sort='metadata'->'createdDate' asc` +
        `&query=[[{"field": "'assetId'", "op": "=", "value": "${formId}" }, {"field": "'jobType'", "op": "=", "value": "form_reminders" }]]`
    )
      .then(response => {
        const results = response.results;

        // if this is the initial loading of the reminders we want to sort them
        if (isInit) {
          sort(results, sortBy);
        }

        // results.forEach(reminderData => {
        //   delete reminderData.metadata; // remove metadata
        //   // delete reminderData.whoSpecificIds; // maybe we'll want to use it in the future
        // });

        dispatch({ type: 'load', payload: results });
      })
      .then(() => setIsLoading(false));
  }, []);

  const loadFormAdmins = useCallback(() => {
    fetchAPI(
      `/forms/v1/forms/${form.id}/collaborate?limit=500&addUserInfo=true`
    ).then(response => {
      let uniqueResults = [],
        foundIds = [];
      if (response && response.results) {
        response.results.forEach(one => {
          if (foundIds.indexOf(one.user_id) === -1) {
            uniqueResults.push(one);
            foundIds.push(one.user_id);
          }
        });
      }
      setFormAdmins(uniqueResults);
    });
  }, [form]);

  // is used for duplicate as well
  const addNewReminder = useCallback(
    data => {
      setIsLoading(true);
      const duplicationIndex = reminders.findIndex(
        reminderData => reminderData.id === data.id
      );
      const body = JSON.parse(JSON.stringify(data));

      if (body.condition && body.condition.statusList) {
        const statusList = body.condition.statusList;
        if (statusList.includes('all')) {
          body.condition.statusList = [];
        }
      }

      fetchAPI(`/scheduler/v1/schedule`, {
        method: 'POST',
        body: body,
        handleResponse: response => {
          const id = response.headers.map.location;
          body.id = id;
          if (duplicationIndex < 0) {
            setNewReminderId(id);
          }
          dispatch({ type: 'add', payload: body, index: duplicationIndex });
          openSnackbar('success', 'snackbars.reminders.addedReminder.success');
        }
      }).finally(() => setIsLoading(false));
    },
    [reminders]
  );

  const duplicateReminder = useCallback(
    data => {
      setIsLoading(true);
      const newReminderData = JSON.parse(JSON.stringify(data));
      //Nullify last Run, otherwise the reminder may not run.
      newReminderData.lastRun = undefined;
      newReminderData.isActive = false;
      if (newReminderData.title !== '') {
        const name = newReminderData.title;
        const allNames = reminders.map(reminderData => reminderData.title);
        newReminderData.title = generateDuplicatedReminderName(allNames, name);
      }

      if (newReminderData.additionalData) {
        let additionalData = JSON.parse(newReminderData.additionalData);
        if (additionalData.listVariableValue) {
          delete additionalData.listVariableValue;
        }
        newReminderData.additionalData = JSON.stringify(additionalData);
      }
      if (newReminderData.emailTemplateId) {
        fetchAPI(
          `/mailer/v1/emails/${newReminderData.emailTemplateId}/duplicate`,
          {
            method: 'POST',
            handleResponse: r => {
              return r.headers.map.location;
            }
          }
        ).then(location => {
          const newTemplateId = location
            ? location.replace('mailer/', '')
            : undefined;
          newReminderData.emailTemplateId = newTemplateId;
          addNewReminder(newReminderData);
        });
      } else {
        addNewReminder(newReminderData);
      }
    },
    [reminders, addNewReminder]
  );

  const deleteReminder = useCallback(reminderId => {
    fetchAPI(`/scheduler/v1/schedule/${reminderId}`, {
      method: 'DELETE'
    })
      .then(() => {
        dispatch({ type: 'delete', payload: reminderId });
        openSnackbar('success', 'snackbars.reminders.deleteSuccess');
      })
      .catch(e => {
        openSnackbar('error', 'snackbars.reminders.server_error');
        log(e, 'Error');
      })
      .finally(() => setIsLoading(false));
  }, []);

  const updateReminder = useCallback((reminderData, snackbarMsg) => {
    dispatch({ type: 'update', payload: reminderData });

    const body = Object.assign({}, reminderData);

    if (body.condition && body.condition.statusList) {
      const statusList = body.condition.statusList;
      if (statusList.includes('all')) {
        body.condition.statusList = [];
      }
    }

    fetchAPI(`/scheduler/v1/schedule/${reminderData.id}`, {
      body,
      method: 'PUT'
    })
      .then(() => {
        if (snackbarMsg) {
          openSnackbar('success', snackbarMsg);
        }
      })
      .catch(reason => {
        openSnackbar('error', 'snackbars.reminders.server_error');
        log(reason, 'Error');
      });
  }, []);

  const onEditEmailClicked = useCallback(data => {
    setReminderData4TemplateEditor({ ...data });
  }, []);

  const allDateFields = useMemo(() => {
    const endUserDateFields =
      (form &&
        form.fields &&
        form.fields.filter(f => f.uiType === 'date' && !f.archived)) ||
      [];
    const adminDateFields =
      (form &&
        form.adminFields &&
        form.adminFields.filter(f => f.uiType === 'date' && !f.archived)) ||
      [];
    const allDateFields = endUserDateFields.concat(adminDateFields);

    const lastUpdated = {
      title: 'Last updated date',
      internalId: 'updatedDate'
    };
    allDateFields.unshift(lastUpdated);

    return allDateFields;
  }, [form]);

  const validateReminderTitle = useCallback(
    (title, id) =>
      title === ''
        ? true
        : reminders.every(reminderData =>
            reminderData.id !== id ? reminderData.title !== title : true
          ),
    [reminders]
  );

  const onSortingChanged = useCallback(
    value => {
      setValueFromLocalStorage('reminders-sort', value);
      setSortBy(value);
      sort(reminders, value);
      sort(filteredReminders, value);
    },
    [reminders, filteredReminders, setSortBy]
  );

  const api = {
    form,
    formAdmins,
    allDateFields,
    updateReminder,
    deleteReminder,
    addNewReminder,
    duplicateReminder,
    onEditEmailClicked,
    validateReminderTitle
  };

  const viewProps = {
    reminders,
    isCreateNewReminderDialogOpen,
    setIsCreateNewReminderDialogOpen,
    isLoading,
    newReminderId,
    reminderData4TemplateEditor,
    onSortingChanged,
    sortBy,
    setSearchValue,
    filteredReminders,
    initialValue: searchValue
  };

  return (
    <GeneralContext.Provider value={api}>
      <Loader isLoading={isLoading} />
      <FormBuilderRemindersView {...viewProps} />
    </GeneralContext.Provider>
  );
};

export default FormBuilderRemindersEngine;
